import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Sparkles, Flame, Clock } from "lucide-react";
import { toast } from "sonner";
import CommentItem from "@/components/CommentItem";

/**
 * Comment thread under each Encyclopedia module.
 * - Any signed-in user can post their "takeaway".
 * - Anyone can react with a like; backend dedupes per user.
 * - Sort by latest or most-liked.
 */
export default function EncyclopediaComments({ moduleId }) {
  const { isRTL } = useI18n();
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sort, setSort] = useState("top"); // 'top' or 'new'
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["encyclopedia", "comments", moduleId, sort],
    queryFn: () => endpoints.listEncyclopediaComments(moduleId, sort),
    enabled: !!moduleId,
  });

  // Scroll to a specific comment when URL hash points at it.
  useEffect(() => {
    if (!comments?.length) return;
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    const m = hash.match(/^#comment-(.+)$/);
    if (!m) return;
    const el = document.getElementById(`comment-${m[1]}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#C86240]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#C86240]", "ring-offset-2"), 2400);
      }, 250);
    }
  }, [comments?.length]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["encyclopedia", "comments", moduleId] });
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      toast.error(isRTL ? "اكتب جملة قصيرة على الأقل" : "Write a short sentence");
      return;
    }
    setSubmitting(true);
    try {
      await endpoints.createEncyclopediaComment({ module_id: moduleId, text: trimmed });
      setText("");
      invalidateAll();
      toast.success(isRTL ? "تم نشر رأيك" : "Posted!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر النشر" : "Failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك للرد" : "Sign in to reply");
      return;
    }
    const t = replyText.trim();
    if (t.length < 3) {
      toast.error(isRTL ? "اكتب جملة قصيرة" : "Write a short sentence");
      return;
    }
    setReplySubmitting(true);
    try {
      await endpoints.createEncyclopediaComment({ module_id: moduleId, text: t, parent_id: parentId });
      setReplyText("");
      setReplyTo(null);
      invalidateAll();
      toast.success(isRTL ? "تم نشر الرد" : "Reply posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر النشر" : "Failed"));
    } finally {
      setReplySubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا التعليق؟" : "Delete this comment?")) return;
    try {
      await endpoints.deleteEncyclopediaComment(id);
      invalidateAll();
      toast.success(isRTL ? "حُذف" : "Deleted");
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const like = async (id) => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك للتفاعل" : "Sign in to react");
      return;
    }
    // Optimistic update
    qc.setQueriesData({ queryKey: ["encyclopedia", "comments", moduleId] }, (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((c) =>
        c.id === id
          ? { ...c, liked: !c.liked, likes: (c.likes || 0) + (c.liked ? -1 : 1) }
          : c
      );
    });
    try {
      await endpoints.likeEncyclopediaComment(id);
    } catch {
      invalidateAll();
      toast.error(isRTL ? "تعذّر الإعجاب" : "Like failed");
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-[#2A2421]/10" data-testid={`encyclopedia-comments-${moduleId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#C86240]" />
          <h4 className="text-sm uppercase tracking-[0.2em] text-[#2A2421]">
            {isRTL
              ? `ايش الي كان جديد عليك؟ ايش استفدت؟ (${comments.length})`
              : `What was new to you? What did you learn? (${comments.length})`}
          </h4>
        </div>
        <div className="inline-flex rounded-full border border-[#2A2421]/15 p-0.5 bg-white text-xs">
          <button
            onClick={() => setSort("top")}
            className={`px-3 py-1 rounded-full transition-colors flex items-center gap-1 ${sort === "top" ? "bg-[#C86240] text-white" : "text-[#5C544F] hover:text-[#2A2421]"}`}
            data-testid={`enc-sort-top-${moduleId}`}
          >
            <Flame className="w-3 h-3" /> {isRTL ? "الأكثر تفاعلاً" : "Top"}
          </button>
          <button
            onClick={() => setSort("new")}
            className={`px-3 py-1 rounded-full transition-colors flex items-center gap-1 ${sort === "new" ? "bg-[#C86240] text-white" : "text-[#5C544F] hover:text-[#2A2421]"}`}
            data-testid={`enc-sort-new-${moduleId}`}
          >
            <Clock className="w-3 h-3" /> {isRTL ? "الأحدث" : "Latest"}
          </button>
        </div>
      </div>

      {user ? (
        <div className="flex flex-col gap-2 mb-6 bg-[#F9F6F0] rounded-xl p-3 border border-[#2A2421]/8">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              isRTL
                ? "شارك ما كان جديداً عليك في هذا الموديول أو الفكرة الي علقت معك..."
                : "Share what was new to you in this module or the idea that stuck..."
            }
            rows={3}
            maxLength={2000}
            dir={isRTL ? "rtl" : "ltr"}
            className="bg-white border-[#2A2421]/15"
            data-testid={`enc-comment-input-${moduleId}`}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#5C544F]">{text.length}/2000</span>
            <Button
              onClick={submit}
              disabled={submitting || text.trim().length < 3}
              size="sm"
              className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full"
              data-testid={`enc-comment-submit-${moduleId}`}
            >
              <Send className="w-3.5 h-3.5 me-1" /> {isRTL ? "نشر" : "Post"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-[#5C544F] bg-[#F9F6F0] rounded-xl p-3 mb-6 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#C86240]" />
          {isRTL ? "سجّل دخولك حتى تشارك ما تعلمت مع باقي المتعلمين." : "Sign in to share what you learned with other learners."}
        </div>
      )}

      <div className="space-y-3">
        {isLoading && <div className="text-sm text-[#5C544F]">…</div>}
        {!isLoading && (() => {
          const topLevel = comments.filter((c) => !c.parent_id);
          const repliesByParent = comments.reduce((acc, c) => {
            if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
            return acc;
          }, {});
          const replyState = {
            replyTo,
            replyText,
            setReplyText,
            replySubmitting,
            openReply: (id) => { setReplyTo(replyTo === id ? null : id); setReplyText(""); },
            cancelReply: () => { setReplyTo(null); setReplyText(""); },
            submitReply,
          };
          if (topLevel.length === 0) {
            return (
              <div className="text-sm text-[#5C544F] italic">
                {isRTL ? "كن أول من يشارك ما تعلم." : "Be the first to share."}
              </div>
            );
          }
          return topLevel.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              replies={repliesByParent[c.id] || []}
              isReply={false}
              isRTL={isRTL}
              user={user}
              isAdmin={isAdmin}
              testidPrefix="enc-comment"
              replyState={replyState}
              onLike={like}
              onDelete={remove}
              renderChild={(r) => (
                <CommentItem
                  key={r.id}
                  comment={r}
                  isReply={true}
                  isRTL={isRTL}
                  user={user}
                  isAdmin={isAdmin}
                  testidPrefix="enc-comment"
                  replyState={replyState}
                  onLike={like}
                  onDelete={remove}
                  renderChild={() => null}
                />
              )}
            />
          ));
        })()}
      </div>
    </div>
  );
}
